import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import moment from 'moment';

import { API_BASE_URL } from '../../config';

import GridItem from '@/components/Grid/GridItem';
import FacilitySelect from '@/components/FacilitySelect';

import GridContainer from '@/components/Grid/GridContainer';

import Card from '@/components/Card/Card';
import SearchIcon from '@mui/icons-material/Search';

import CardBody from '@/components/Card/CardBody';
import Button from '@/components/CustomButtons/Button';
import posthog from 'posthog-js';

import loader from '../../assets/img/loader.svg';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import ReportPreview from './ReportPreview';
import { checkPermission } from '@/views/Facilities/CheckPermissions';
import TransmittedFilesHistory from '@/components/TransmittedFilesHistory/TransmittedFilesHistory';

export default function Payments() {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [refundReport, setRefundReport] = useState([]);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [dataMappings, setDataMappings] = useState([]);
  const [selectedMapping, setSelectedMapping] = useState('');
  const [status, setStatus] = useState('all');
  const [facilityId, setFacilityId] = useState('');
  const selectstatus = (event) => {
    setStatus(event.target.value);
  };

  const isNewRefundReportingEnabled = posthog.isFeatureEnabled(
    'new-refund-reporting',
  );

  const changeDate = (event) => {
    const endDateFormatted = new Date(startDate);
    const startDateFormatted = new Date(event);

    if (startDateFormatted > new Date()) {
      return toast.error(t('refundReporting.errors.startDateFuture'));
    }
    if (!endDate) return setStartDate(event ?? '');
    if (endDateFormatted > startDateFormatted) {
      toast.error(t('refundReporting.errors.startDateGreater'));
      return;
    }
    setStartDate(event ?? '');
  };

  /*** Change end date */
  const changeEndDate = (event) => {
    const endDateFormatted = new Date(event);
    const startDateFormatted = new Date(startDate);
    if (endDateFormatted > new Date()) {
      return toast.error(t('refundReporting.errors.endDateFuture'));
    }
    if (!startDate) return setEndDate(event ?? '');
    if (endDateFormatted < startDateFormatted) {
      toast.error(t('refundReporting.errors.endDateSmaller'));
      return;
    }
    setEndDate(event ?? '');
  };

  const headers = [];

  if (refundReport.length > 0) {
    for (const key of Object.keys(refundReport[0])) {
      headers.push({ label: key, key: key });
    }
  }

  const getReport = async () => {
    try {
      setLoaded(true);

      if (isNewRefundReportingEnabled && !selectedMapping) {
        setLoaded(false);
        return;
      }

      const endpoint = isNewRefundReportingEnabled
        ? `/data-mappings/generate-report/${selectedMapping}`
        : `/getreportforfacility`;
      let url = `${API_BASE_URL}${endpoint}?status=${status}&search=${search}`;
      if (facilityId) {
        url += `&facilityId=${facilityId}`;
      }
      if (startDate) {
        url += `&startDate=${moment(startDate).format('YYYY-MM-DD')}`;
      }
      if (endDate) {
        url += `&endDate=${moment(endDate).format('YYYY-MM-DD')}`;
      }
      const response = await axios.get(url);
      setLoaded(false);

      setRefundReport(response.data);
    } catch (err) {
      setLoaded(false);
      console.log(err);
    }
  };

  const onSearchChange = (e) => {
    setSearch(e.target.value);
  };
  const clearAllInputs = () => {
    setStartDate('');
    setEndDate('');
    setStatus('all');
    setSearch('');
    setFacilityId('');
  };
  useEffect(() => {
    getReport();
  }, [startDate, endDate, status, search, selectedMapping, facilityId]);

  const downloadFile = async () => {
    try {
      setLoaded(true);
      const endpoint = isNewRefundReportingEnabled
        ? `/data-mappings/generate-report-download/${selectedMapping}`
        : `/getreportforfacility_download`;
      let url = `${API_BASE_URL}${endpoint}?status=${status}&search=${search}&download=true`;
      if (facilityId) {
        url += `&facilityId=${facilityId}`;
      }
      if (startDate) {
        url += `&startDate=${moment(startDate).format('YYYY-MM-DD')}`;
      }
      if (endDate) {
        url += `&endDate=${moment(endDate).format('YYYY-MM-DD')}`;
      }

      const link = document.createElement('a');
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setLoaded(false);
    } catch (err) {
      setLoaded(false);
      console.log(err);
    }
  };

  const fetchDataMappings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/data-mappings/`);

      const dataMappingsWithReport = response.data.filter(
        (mapping) => mapping.outbound_reporting,
      );

      setDataMappings(dataMappingsWithReport);
      if (response.data.length > 0) {
        setSelectedMapping(dataMappingsWithReport.at(0)?.id ?? '');
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (!isNewRefundReportingEnabled) return;

    fetchDataMappings();
  }, [isNewRefundReportingEnabled]);

  const handleMappingChange = (event) => {
    setSelectedMapping(event.target.value);
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <h2 className='subHeading'>{t('refundReporting.title')}</h2>
        <Card
          style={{ height: 'max-content', minHeight: '50vh' }}
          className='dashboardCard'>
          <CardBody style={{ height: 'max-content' }}>
            <div
              onClick={clearAllInputs}
              style={{
                position: 'absolute',
                right: '30px',
                textDecoration: 'underline',
                fontWeight: 'bold',
                color: '#3AB795',
                cursor: 'pointer',
              }}>
              {t('refundReporting.buttons.clearResults')}{' '}
            </div>
            <div style={{ marginTop: '15px' }}>
              {loaded && (
                <div className='loaders'>
                  <img src={loader} />
                </div>
              )}
              <div className='t-header'>
                <div>
                  {/* <CSVLink
                    {...csvReport2}
                    style={{
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                    }}
                    onClick={aggregatereports}>
                    {' '}
                    Download Aggregate Report
                  </CSVLink> */}
                </div>
                <div>
                  {t('refundReporting.dateFilters.from')}&nbsp;&nbsp;
                  <DatePicker
                    className='full-width-elem-date-picker'
                    id='dt'
                    autoComplete='off'
                    selected={startDate}
                    onChange={changeDate}
                  />
                </div>
                <div>
                  {t('refundReporting.dateFilters.to')}&nbsp;&nbsp;
                  <DatePicker
                    className='full-width-elem-date-picker'
                    id='dt'
                    autoComplete='off'
                    selected={endDate}
                    onChange={changeEndDate}
                  />
                </div>

                <FacilitySelect
                  value={facilityId}
                  onChange={setFacilityId}
                  parentKey='refund_reporting'
                  childKey='refund_reporting'
                  permission='read'
                />

                {!isNewRefundReportingEnabled && (
                  <div className='t-header-right'>
                    <div className='custom-search-box'>
                      <div
                        className='custom-select'
                        style={{ marginTop: '10px' }}>
                        <select
                          id='status'
                          className='full-width-elem'
                          value={status}
                          onChange={selectstatus}>
                          <option value='all'>
                            {t('refundReporting.dropdowns.all')}
                          </option>
                          <option value='initiated'>
                            {t('refundReporting.dropdowns.initiated')}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
                {isNewRefundReportingEnabled && (
                  <div className='t-header-right'>
                    <div className='custom-search-box'>
                      <div
                        className='custom-select'
                        style={{ marginTop: '10px' }}>
                        <select
                          id='status'
                          className='full-width-elem'
                          value={selectedMapping}
                          onChange={handleMappingChange}>
                          <option value=''>
                            {t('refundReporting.dropdowns.selectDataMapping')}
                          </option>
                          {dataMappings.map((mapping) => (
                            <option key={mapping.id} value={mapping.id}>
                              {mapping.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  {!loaded && (
                    <Button
                      style={{ marginRight: '10px' }}
                      color='primary'
                      onClick={() => setIsPreviewOpen(true)}>
                      {t('refundReporting.buttons.viewReport')}
                    </Button>
                  )}

                  {checkPermission({
                    parentKey: 'refund_reporting',
                    childKey: 'refund_reporting',
                    permission: 'download',
                  }) && (
                    <Button
                      color='primary'
                      style={{ marginRight: '10px' }}
                      onClick={() => {
                        downloadFile();
                        toast.success(
                          t('refundReporting.toasts.downloadingReport'),
                          {
                            position: 'bottom-right',
                            autoClose: 5000,
                          },
                        );
                      }}>
                      {t('refundReporting.buttons.downloadReport')}
                    </Button>
                  )}
                </div>

                <div className='t-header-right' style={{ padding: '10px' }}>
                  <div className='custom-search-box'>
                    <input
                      type='search'
                      className='seacrh-field'
                      value={search}
                      onChange={onSearchChange}
                      placeholder={t('refundReporting.search.placeholder')}
                    />
                    <span className='search-icon'>
                      <SearchIcon />
                    </span>
                  </div>
                </div>
              </div>
              {isPreviewOpen && (
                <main style={{ height: '500px', width: '100%' }}>
                  <ReportPreview refundReport={refundReport} />
                </main>
              )}
            </div>

            {(checkPermission({
              parentKey: 'refund_reporting',
              childKey: 'transmitted_files',
              permission: 'read',
            }) ||
              checkPermission({
                parentKey: 'refund_reporting',
                childKey: 'refund_reporting',
                permission: 'read',
              })) && <TransmittedFilesHistory />}
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
